import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from '../../contexts/AuthContext';
import { Table } from '../common/Table';
import {Loading} from '../common/Loading';


const apiUrl = window._env_?.REACT_APP_API_URL || "https://app.mylocaleventrentals.com/api";

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  return isNaN(date.getTime()) ? value : date.toLocaleDateString('en-CA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const EnquiryList = () =>{

    const navigate = useNavigate();
    const { logout } = useAuth();
    const token = localStorage.getItem("token");
    const [loading, setLoading] = useState(true);
    const [enquires, setEnquires] = useState([]);
    const columns = [
    { key: 'quoteNumber', label: 'Quote #', sortable: false },
    { key: 'createdAt', label: 'Date', sortable: true, render: formatDate },
    { key: 'customerName', label: 'Name', sortable: false },
    { key: 'customerEmail', label: 'Email', sortable: false },
    { key: 'customerPhone', label: 'Phone', sortable: false },
    { key: 'eventType', label: 'Event Type', sortable: false },
    { key: 'source', label: 'Source', sortable: false },
    { key: 'eventDate', label: 'Event Date', sortable: false, render: formatDate },
    { 
      key: 'status', 
      label: 'Status', 
      sortable: true,
      editable: true, 
      editType: 'select',
      options: [
        { value: 'new', label: 'New' },
        { value: 'contacted', label: 'Contacted' },
        { value: 'quoted', label: 'Quoted' },
        { value: 'confirmed', label: 'Confirmed' },
        { value: 'cancelled', label: 'Cancelled' }
      ],
      render: (value) => {
        const statusColors = {
          pending: 'bg-yellow-100 text-yellow-800',
          confirmed: 'bg-blue-100 text-blue-800',
          delivered: 'bg-green-100 text-green-800',
          cancelled: 'bg-red-100 text-red-800',
          completed: 'bg-purple-100 text-purple-800'
        };
        return (
          <span className={`px-2 py-1 rounded text-xs ${statusColors[value] || 'bg-gray-100 text-gray-800'}`}>
            {value}
          </span>
        );
      }
    },
   {
      key: 'actions',
      label: 'Actions',
      render: (value, row, { isEditing, handleEdit, handleSave, handleCancel }) => {
        if (isEditing) {
          return (
            <div className="flex gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleSave(row);
                }}
                className="px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
              >
                ✓ Save
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleCancel();
                }}
                className="px-3 py-1 text-xs bg-gray-600 text-white rounded hover:bg-gray-700"
              >
                ✕ Cancel
              </button>
            </div>
          );
        }

        return (
          <div className="flex gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(row);
              }}
              className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Edit
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteItem(row);
              }}
              className="px-3 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
            >
              Delete
            </button>
          </div>
        );
      }
    }
  ];

    const handleSave = async (data) => {
    if (!token) return;
   
    try {
      await axios.put(`${apiUrl}/enquiry/${data.uuid}`, data, {
        headers: { "x-auth-token": token },
      });
      setEnquires((prevPayments) =>
        prevPayments.map((p) => (p.uuid === data.uuid ? data : p))
      );
    } catch (err) {
      console.error("Error updating enquiry:", err);
      if (err.response?.status === 401) {
        await logout();
        navigate('/login', { replace: true });
      }
    }
  };

  const handleDeleteItem = async (enquiry) => {
    if (!token) return;
    const confirmed = window.confirm(
      `Delete enquiry for "${enquiry.customerName || enquiry.customerEmail || "this enquiry"}"?`
    );
    if (!confirmed) return;
    try {
      await axios.delete(`${apiUrl}/enquiry/${enquiry.uuid}`, {
        headers: { "x-auth-token": token },
      });
      setEnquires((prev) => prev.filter((e) => e.uuid !== enquiry.uuid));
    } catch (err) {
      console.error("Error deleting enquiry:", err);
      if (err.response?.status === 401) {
        await logout();
        navigate('/login', { replace: true });
      }
    }
  };

  useEffect(() => {
    if (!token) {
      logout().then(() => navigate('/login', { replace: true }));
    }
  }, [token,logout,navigate]);

    const fetchEnquiries = async () => {
  if (!token) return;
  try {
    const res = await axios.get(`${apiUrl}/enquiry?limit=1000`, {
      headers: { "x-auth-token": token },
    });

    setEnquires(res.data.enquiries);
  } catch (err) {
    console.error("Error fetching payments:", err);
    if (err.response?.status === 401) {
      await logout();
      navigate('/login', { replace: true });
    }
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    if (token) {
      fetchEnquiries();
    }
  }, [token]);


    if (loading) {
        return <Loading />;
    }


    return(
        <div className="p-4 md:p-6 my-2 md:my-6 bg-white shadow-lg rounded-lg">
            <h2 className="text-2xl md:text-3xl font-bold mb-6 text-gray-800">All Enquiries</h2>
            <Table
                columns={columns}
                data={enquires}
                rowKey="uuid"
                striped={true}
                paginated={true}
                pageSize={15}
                onRowClick={(enquiry) => navigate(`/enquiries/${enquiry.uuid}`)}
                hoverable={true}
                onSave={handleSave}
            />
        </div>
    );

};

export default EnquiryList;